﻿class BannerTimer extends HTMLElement {
  connectedCallback() {
    this._endDate = this.dataset.endDate;
    this._rafId = null;
    this._intervalId = null;
    this._wraps = [];
    this._bound = this._onScroll.bind(this);
    this._boundResize = this._onScroll.bind(this);
    this._initCountdown();
    this._initParallax();
  }

  disconnectedCallback() {
    if (this._intervalId) clearInterval(this._intervalId);
    if (this._rafId) cancelAnimationFrame(this._rafId);
    window.removeEventListener('scroll', this._bound);
    window.removeEventListener('resize', this._boundResize);
  }

  _initCountdown() {
    if (!this._endDate) return;
    const endMs = new Date(this._endDate.replace(' ', 'T')).getTime();
    if (isNaN(endMs)) return;

    const unitEls = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };

    const pad = (n) => String(n).padStart(2, '0');

    const tick = () => {
      const diff = endMs - Date.now();
      if (diff <= 0) {
        clearInterval(this._intervalId);
        Object.values(unitEls).forEach((el) => {
          if (el) el.textContent = '00';
        });
        return;
      }
      const s = Math.floor(diff / 1000);
      if (unitEls.days) unitEls.days.textContent = pad(Math.floor(s / 86400));
      if (unitEls.hours)
        unitEls.hours.textContent = pad(Math.floor((s % 86400) / 3600));
      if (unitEls.minutes)
        unitEls.minutes.textContent = pad(Math.floor((s % 3600) / 60));
      if (unitEls.seconds) unitEls.seconds.textContent = pad(s % 60);
    };

    tick();
    this._intervalId = setInterval(tick, 1000);
  }

  _initParallax() {
    this._wraps = Array.from(this.querySelectorAll('[data-parallax]'));
    if (!this._wraps.length) return;

    // Larger movement range for a more visible parallax effect.
    this._strengths = [120, 120];
    // Easing factor for smooth motion and less jitter.
    this._lerp = 0.12;
    this._targets = new WeakMap();
    this._currents = new WeakMap();

    this._wraps.forEach((wrap) => {
      this._targets.set(wrap, 0);
      this._currents.set(wrap, 0);
    });

    window.addEventListener('scroll', this._bound, { passive: true });
    window.addEventListener('resize', this._boundResize, { passive: true });
    this._onScroll();
  }

  _onScroll() {
    if (!this._wraps.length) return;
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight || 1;
    // 0 at center, negative/positive toward edges.
    const progress = (vh / 2 - (rect.top + rect.height / 2)) / vh;

    this._wraps.forEach((wrap) => {
      const idx = parseInt(wrap.dataset.parallax, 10) || 0;
      const strength = this._strengths[idx] ?? this._strengths[0] ?? 120;
      const target = progress * strength;
      this._targets.set(wrap, target);
    });

    if (!this._rafId) {
      this._rafId = requestAnimationFrame(() => this._animateParallax());
    }
  }

  _animateParallax() {
    let needsNextFrame = false;
    this._wraps.forEach((wrap) => {
      const target = this._targets.get(wrap) ?? 0;
      const current = this._currents.get(wrap) ?? 0;
      const next = current + (target - current) * this._lerp;

      this._currents.set(wrap, next);
      wrap.style.setProperty('--parallax-y', `${next.toFixed(2)}px`);

      if (Math.abs(target - next) > 0.1) {
        needsNextFrame = true;
      }
    });

    if (needsNextFrame) {
      this._rafId = requestAnimationFrame(() => this._animateParallax());
      return;
    }

    this._rafId = null;
  }
}

if (!customElements.get('banner-timer')) {
  customElements.define('banner-timer', BannerTimer);
}
